import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Stack,
    Tooltip,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import type {SelectChangeEvent} from "@mui/material/Select";
import {MbscCalendarEvent, MbscResource} from "@mobiscroll/react";
import {
    ReservationActions,
    ReservationDaySlice,
    useReservationsState
} from "../../../store/reservations.reducer";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    calculateEarliestReservationByDateRange,
    calculateEndDateWithTimelineSpan
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {
    useCurrentApp,
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useDispatch} from "react-redux";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    useListAgendas
} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {
    ServiceService
} from "crqlar-react-common/dist/services/service/service.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useBookingEngineConfigs} from "crqlar-react-common/dist/utils/hooks/bookingEngine/useBookingEngineConfigs";
import {persistSectorDate, readSectorDate} from "../sectorDate";
import {slotDayPrefix, useResourceOccupancy} from "./util/useResourceOccupancy";
import AgendaDropDown from "./AgendaDropDown";
import SingleDatePicker from "../../common/newDatePicker/SingleDatePicker";
import type {DatePickerChangeReason} from "../../common/newDatePicker/datePickerShared";
import {RightSidebarRailHeaderCenter} from "../../layout/rightSidebar/RightSidebarRail";

interface Props {
    // Accepted only by the compiled legacy timeline backup. The active calendar
    // owns search in its right rail and does not pass these props.
    searchSidebarOpen?: boolean
    setSearchSidebarOpen?: (open: boolean) => void
    navigatedDate: Date | undefined
    reservations: Reservation[]
    services: Service[]
    reservationDaySlice?: ReservationDaySlice | undefined
    timelineSpan?: "day" | "week" | "month" | undefined
    mbscResources?: MbscResource[] | null
    mbscEvents?: MbscCalendarEvent[]
}

const CalendarHeader = (props: Props) => {
    const {t} = useLocalize()
    const appId = useCurrentAppId()
    const appData = useCurrentApp()
    const sector = useSector(appData?.configuration?.sectorId)
    const organization = useOrganization()

    const dispatch = useDispatch()

    // The sector-wide day, shared with the floor plan and the day lists.
    const rangeFromLocalStorage = readSectorDate(sector?.id)?.date

    const persistSingleDate = (date: Date, reason: DatePickerChangeReason) => {
        persistSectorDate(sector?.id, {
            date,
            mode: reason === "today" ? "today" : "fixed",
        })
    }

    const {
        activeTab,
        fromDate,
        timelineSpan,
        timelineStep,
    } = useReservationsState(appId)

    const finalFromDate = rangeFromLocalStorage ?? fromDate

    // The date currently shown in the field, normalised to a JS Date for day math / comparisons
    const currentDate = useMemo(() => {
        const value = finalFromDate || props.navigatedDate
        if (!value) return new Date()
        return value instanceof Date ? value : new Date(value as string)
    }, [finalFromDate, props.navigatedDate])

    // Navigate the calendar to an arbitrary date (used by the date picker and the day arrows)
    const navigateToDate = (date: Date, reason: DatePickerChangeReason) => {
        const endDate = calculateEndDateWithTimelineSpan(date, timelineSpan)
        const navigatedDate = calculateEarliestReservationByDateRange(
            date,
            endDate as Date,
            props.reservations as Reservation[]
        )

        if (!navigatedDate) return

        // The reservation query is keyed by the complete visible range. Updating only the
        // start left `toDate` on the previous page until Mobiscroll emitted its follow-up
        // page-change event, producing an inverted range and an empty calendar.
        const navigatedEndDate = calculateEndDateWithTimelineSpan(navigatedDate, timelineSpan)
            ?? endDate
            ?? navigatedDate

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                fromDate: navigatedDate.toISOString(),
                toDate: navigatedEndDate.toISOString(),
            }
        })

        // Persist to local storage in order to keep the range after page refresh
        persistSingleDate(date, reason)
    }

    // Restaurant occupancy threshold alert
    const isRestaurant = sector?.sectorType === SectorType.RESTAURANT
    const isWeeklyPlanner = sector?.sectorType === SectorType.WEEKLY_PLANNER
    const bookingEnginesForSector = useBookingEngineConfigs(
        isRestaurant && sector?.id ? [sector.id] : undefined
    )
    const occupancyThreshold = bookingEnginesForSector[0]?.confirmationConfig?.occupancyThreshold

    const { occupancyBySlot } = useResourceOccupancy({
        mbscResources: props.mbscResources,
        events: props.mbscEvents,
        timelineStep: timelineStep ?? 30,
        sector
    })

    // Fetching the already existingg occupancy occupancy per slot to decide whether to activate the warning.
    // It reads one day's peak, so it only shows on a day page (a week would show Monday's).
    const { isOccupancyAboveThreshold, maxOccupancyPercentage } = useMemo(() => {
        if (!isRestaurant || !finalFromDate || timelineSpan !== 'day') return { isOccupancyAboveThreshold: false, maxOccupancyPercentage: 0 }
        const d = finalFromDate instanceof Date ? finalFromDate : new Date(finalFromDate as string)
        const dayPrefix = slotDayPrefix(d)
        let max = 0
        occupancyBySlot.forEach((value, key) => {
            if (key.startsWith(dayPrefix)) {
                const pct = parseInt(value, 10)
                if (pct > max) max = pct
            }
        })
        return {
            isOccupancyAboveThreshold: !!occupancyThreshold && max >= occupancyThreshold,
            maxOccupancyPercentage: max
        }
    }, [occupancyBySlot, occupancyThreshold, finalFromDate, isRestaurant, timelineSpan])

    const agendas = useListAgendas()

    // always keep this as plain string to avoid TS union issues
    const [selectedAgendaId, setSelectedAgendaId] = useState<string>("")

    // on load (and when agendas change), read from localStorage, fall back to first,
    // update state, persist, and dispatch
    useEffect(() => {
        if (!sector?.id) return
        if (!agendas.data || agendas.data.length === 0) return
        if (sector.sectorType !== SectorType.WEEKLY_PLANNER) return

        const agendaKey = `selectedAgenda-${sector.id}`
        const storedAgendaId = localStorage.getItem(agendaKey) || undefined

        const storedAgenda = storedAgendaId
            ? agendas.data.find(a => a.id === storedAgendaId)
            : undefined

        const effectiveAgenda = storedAgenda || agendas.data[0]
        if (!effectiveAgenda) return

        // sync local state and storage
        if (selectedAgendaId !== effectiveAgenda.id) {
            setSelectedAgendaId(effectiveAgenda.id!)
        }
        localStorage.setItem(agendaKey, effectiveAgenda.id!)

        // dispatch on load / when agendas list resolves
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                selectedAgenda: effectiveAgenda,
            }
        })
    }, [agendas.data, sector?.id, sector?.sectorType, appId, dispatch, selectedAgendaId])


    useEffect(() => {
        // for weekly planner we need services directly
        if (sector?.sectorType !== SectorType.WEEKLY_PLANNER) return

        const value = "services"
        const resourceCategoryKey = `resourceCategoryFilter-${sector?.id}`
        localStorage.setItem(resourceCategoryKey, value)
        localStorage.setItem(`mbscResourceColumnType-${sector?.id}`, "services")

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                resourceCategoryFilter: value,
                mbscResourceColumnType: "services"
            }
        })
    }, [])


    // TODO: the following should be combined into 1 logic peace to load all the selected services once on component mount
    const handleChangeAgenda = async (e: SelectChangeEvent<string>) => {
        const newId = e.target.value
        setSelectedAgendaId(newId)

        const agenda = agendas.data?.find(a => a.id === newId)
        const agendaKey = `selectedAgenda-${sector?.id}`

        if (sector?.id && newId) {
            localStorage.setItem(agendaKey, newId)
        }

        const newServiceIds = agenda?.serviceIds ?? []
        const response = await ServiceService.getAllServices({
            id: newServiceIds,
            limit: {
                offset: 0,
                limit: 1000,
                sort: [{field: "name", direction: "ASC"}]
            }
        })

        const newServices = response.content || []
        let key = `selectedServices-${sector?.id}-${activeTab}`
        // Storing comma separated resource ids as local storage accepts only string value
        localStorage.setItem(
            key, newServices
                .map(r => r.id)
                .join(',')
        )

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId: appId,
            payload: {
                selectedAgenda: agenda,
                selectedServices: newServices
            }
        })
    }

    useEffect(() => {
        const key = `selectedServices-${sector?.id}-${activeTab}`;
        const storageServiceIds = localStorage.getItem(key)
        const serviceIds = storageServiceIds ? storageServiceIds.split(',') : [];
        if (!serviceIds.length) return

        const loadServices = async (serviceIds: string[]) => {
            const response = await ServiceService.getAllServices({
                id: serviceIds,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}]
                }
            })

            const newServices = response.content || []
            let key = `selectedServices-${sector?.id}-${activeTab}`
            // Storing comma separated resource ids as local storage accepts only string value
            localStorage.setItem(
                key, newServices
                    .map(r => r.id)
                    .join(',')
            )

            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId: appId,
                payload: {
                    selectedServices: newServices
                }
            })
        }

        loadServices(serviceIds)

    }, []);

    const renderHeaderFilter = () => isWeeklyPlanner
        ? (
            <AgendaDropDown
                organizationName={organization?.name}
                selectedAgendaId={selectedAgendaId}
                rangeFromLocalStorage={rangeFromLocalStorage}
                agendasData={agendas.data}
                handleChangeAgenda={handleChangeAgenda}
            />
        )
        : null

    return (
        <React.Fragment>
            <Stack
                direction={{xs: isWeeklyPlanner ? 'column' : 'row', md: 'row'}}
                alignItems="center"
                justifyContent="flex-start"
                sx={{
                    position: 'relative',
                    width: '100%',
                }}
            >
                {/* Weekly Planner filter / desktop right-side spacer */}
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        display: {xs: isWeeklyPlanner ? 'flex' : 'none', md: 'flex'},
                        order: { xs: 1, md: 3 },
                        flex: { md: 1 },
                        justifyContent: { xs: 'center', md: 'flex-end' },
                        mb: {xs: isWeeklyPlanner ? 1 : 0, md: 0}
                    }}
                >
                    {renderHeaderFilter()}
                </Stack>

                {/* Desktop: left spacer */}
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' }, order: 1 }} />

                {/* Mobile row 2 / Desktop: center */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{xs: 'space-between', md: 'center'}}
                    sx={{
                        width: {xs: '100%', md: 'auto'},
                        order: {xs: 2, md: 2},
                    }}
                >
                    <RightSidebarRailHeaderCenter>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <SingleDatePicker
                                value={currentDate}
                                onChange={navigateToDate}
                                showStepButtons
                                size="medium"
                            />

                            {isRestaurant && isOccupancyAboveThreshold && (
                                <Tooltip
                                    title={t('restaurant_occupancy_limit_alert', {
                                        percentage: maxOccupancyPercentage,
                                        threshold: occupancyThreshold
                                    })}
                                    placement="bottom"
                                    componentsProps={{ tooltip: { sx: { fontSize: '0.95rem' } } }}
                                >
                                    <ReportProblemOutlinedIcon
                                        sx={{ color: 'warning.main', alignSelf: 'center', cursor: 'help', fontSize: '1.5rem', ml: 1.5 }}
                                    />
                                </Tooltip>
                            )}
                        </Stack>
                    </RightSidebarRailHeaderCenter>

                </Stack>
            </Stack>
        </React.Fragment>
    )
}

export default CalendarHeader
